import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { debounce } from "lodash";

/**
 * The Company search on the Item List and the Invoice List.
 *
 *   typed    any part of a company name, any letter case: the list shows the
 *            rows of every company whose name matches (the server does the
 *            matching - backend services/companySearch.js)
 *   picked   one company from the suggestions: the list shows its rows only,
 *            by its id, so "ACME" does not also bring in "ACME TRADERS"
 *   cleared  every company again
 *
 * `onSearch` receives { companyName }, { companyId } or {} accordingly.
 * Typing waits for a pause, like the list's own search box.
 */

/** Suggestions shown at once - a long company list stays quick to open. */
export const SUGGESTION_LIMIT = 50;
export const SEARCH_DELAY_MS = 500;

const filterSuggestions = createFilterOptions({
  limit: SUGGESTION_LIMIT,
  stringify: (company) => company?.companyName || "",
});

const nameOf = (option) =>
  typeof option === "string" ? option : option?.companyName || "";

export const CompanySearchField = ({ companies = [], onSearch }) => {
  const [text, setText] = useState("");

  const searchByName = useMemo(
    () =>
      debounce((name) => {
        onSearch(name.trim() ? { companyName: name } : {});
      }, SEARCH_DELAY_MS),
    [onSearch],
  );
  useEffect(() => () => searchByName.cancel(), [searchByName]);

  return (
    <Autocomplete
      freeSolo
      options={companies}
      filterOptions={filterSuggestions}
      getOptionLabel={nameOf}
      inputValue={text}
      onInputChange={(_event, value, reason) => {
        setText(value);
        if (reason === "input") searchByName(value);
      }}
      onChange={(_event, value) => {
        searchByName.cancel();
        if (value && typeof value === "object") {
          onSearch({ companyId: value._id });
        } else if (typeof value === "string" && value.trim()) {
          // Enter on typed text: search it straight away.
          onSearch({ companyName: value });
        } else {
          onSearch({});
        }
      }}
      renderOption={({ key, ...props }, company) => (
        <li key={company._id || key} {...props}>
          <span>
            {company.companyName}
            {company.ntn ? (
              <span style={{ color: "#888", fontSize: 12 }}>
                {" "}
                / {company.ntn}
              </span>
            ) : null}
          </span>
        </li>
      )}
      sx={{ width: { xs: "100%", sm: 320 } }}
      renderInput={(params) => (
        <TextField {...params} size="small" label="Search by Company" />
      )}
    />
  );
};
